import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ListTodo, MessageSquarePlus, Zap } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, post } from '../../nucleo/api';
import { nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CHAVE, SeletorQuadro } from '../../modules/tarefas/comum';
import { FormCard } from '../../modules/tarefas/FormCard';
import type { Quadro } from '../../modules/tarefas/tipos';
import { usePessoasDoChat } from '../../modules/equipe/dados';
import type { Canal } from '../../modules/equipe/tipos';

export type OpcaoAtalho = 'tarefa' | 'mensagem';

/**
 * U15 F8: o que o atalho rápido oferece. Cada opção só existe com o módulo instalado no workspace e a permissão de criar
 * (Tarefas) ou enviar (Equipe); sem nenhuma, o botão não aparece.
 */
export function opcoesDoAtalho(s: { instalado(slug: string): boolean; pode(permissao: string): boolean }): OpcaoAtalho[] {
  const r: OpcaoAtalho[] = [];
  if (s.instalado('tarefas') && s.pode('tarefas.criar')) r.push('tarefa');
  if (s.instalado('equipe') && s.pode('equipe.enviar')) r.push('mensagem');
  return r;
}

/** Card novo: quadro "demandas" ou o primeiro, como o "Criar tarefa" do chatter; só título, responsável e prazo. */
function NovaTarefa({ aoSalvo }: { aoSalvo: (r: { id?: string }, quadroId: string) => void }) {
  const { t } = useTranslation('tarefas');
  const s = useSessao();
  const [quadroId, setQuadroId] = useState<string | null>(null);
  const quadros = useQuery({ queryKey: [...CHAVE, 'quadros'], queryFn: () => get<{ itens: Quadro[] }>('/tarefas/quadros') });
  const lista = quadros.data?.itens ?? [];
  const quadro = lista.find((q) => q.id === quadroId) ?? lista.find((q) => q.codigo === 'demandas') ?? lista[0];
  return (
    <div className="flex flex-col gap-4">
      {lista.length > 1 && quadro && <SeletorQuadro quadros={lista} valor={quadro.id} aoMudar={setQuadroId} />}
      {quadros.isSuccess && !quadro && <p className="text-sm text-tinta-3">{t('registro.semQuadro')}</p>}
      {quadro && <FormCard key={quadro.id} quadro={quadro} usuarioId={s.usuario.id} resumido aoSalvar={(r) => aoSalvo(r, quadro.id)} />}
    </div>
  );
}

/** Mensagem para uma pessoa (abre a conversa direta) ou para um canal em que a pessoa já está. */
function NovaMensagem({ aoEnviada }: { aoEnviada: (canalId: string) => void }) {
  const { t } = useTranslation();
  const s = useSessao();
  const pessoas = usePessoasDoChat(true);
  const canais = useQuery({ queryKey: ['equipe', 'canais', 'atalho'], queryFn: () => get<{ itens: Canal[] }>('/equipe/canais', { limite: 100 }) });
  // A primeira opção é só o convite ("Escolha..."): sem escolha o campo obrigatório barra o envio (nunca manda a quem veio primeiro).
  const opcoes = [
    { valor: '', rotulo: t('atalho.escolha') },
    ...(pessoas.data?.itens ?? []).filter((p) => p.id !== s.usuario.id).map((p) => ({ valor: `p:${p.id}`, rotulo: nomePessoa(p.nome) })),
    ...(canais.data?.itens ?? []).filter((c) => c.tipo !== 'direta' && c.participa && !c.arquivado).map((c) => ({ valor: `c:${c.id}`, rotulo: `${c.nome ?? ''} · ${t('atalho.canal')}` })),
  ];
  const campos: DefCampo[] = [
    { nome: 'destino', rotulo: t('atalho.para'), tipo: 'selecao', obrigatorio: true, opcoes },
    { nome: 'texto', rotulo: t('atalho.mensagem'), tipo: 'area', obrigatorio: true },
  ];
  return (
    <Formulario
      id="form-atalho-mensagem"
      campos={campos}
      inicial={{ destino: '', texto: '' }}
      aoEnviar={async (v) => {
        const [tipo, id] = String(v.destino).split(':') as ['p' | 'c', string];
        const canalId = tipo === 'p' ? (await post<{ id: string }>('/equipe/diretas', { usuario_id: id })).id : id;
        await post(`/equipe/canais/${canalId}/mensagens`, { texto: String(v.texto).trim(), mencoes: [], anexo_ids: [], resposta_a: null });
        aoEnviada(canalId);
      }}
    />
  );
}

/**
 * U15 F8: atalho rápido do topo. Abre um Drawer "Registrar rápido" (nova tarefa e mensagem para a equipe) sem tirar a pessoa
 * da tela; ao fechar, o Drawer devolve o foco ao botão que o abriu. `celular`: botão só com ícone, do tamanho dos outros
 * botões do cabeçalho.
 */
export function AtalhoRapido({ celular }: { celular?: boolean }) {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const nav = useNavigate();
  const opcoes = opcoesDoAtalho(s);
  const [aberto, setAberto] = useState(false);
  const [escolha, setEscolha] = useState<OpcaoAtalho | null>(null);
  if (!opcoes.length) return null;
  const atual = escolha && opcoes.includes(escolha) ? escolha : opcoes[0]!;
  const fechar = () => {
    setAberto(false);
    setEscolha(null);
  };

  return (
    <>
      {celular ? (
        <button type="button" data-atalho-rapido aria-label={t('atalho.abrir')} aria-haspopup="dialog" title={t('atalho.abrir')} onClick={() => setAberto(true)} className="flex size-11 shrink-0 items-center justify-center rounded-lg text-tinta-2 hover:bg-sup-2">
          <Zap className="size-5" aria-hidden />
        </button>
      ) : (
        <Botao variante="fantasma" tamanho="sm" data-atalho-rapido aria-haspopup="dialog" onClick={() => setAberto(true)} icone={<Zap className="size-4" aria-hidden />}>
          {t('atalho.abrir')}
        </Botao>
      )}
      <Drawer
        aberto={aberto}
        aoFechar={fechar}
        titulo={t('atalho.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={fechar}>
              {t('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form={atual === 'tarefa' ? 'form-card' : 'form-atalho-mensagem'}>
              {atual === 'tarefa' ? t('atalho.salvarTarefa') : t('atalho.enviarMensagem')}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {opcoes.length > 1 && (
            <div role="group" aria-label={t('atalho.oQue')} className="flex flex-wrap gap-2">
              {opcoes.map((o) => (
                <Botao key={o} variante="secundario" data-atalho-opcao={o} aria-pressed={atual === o} className={atual === o ? 'border-acento-forte' : ''} onClick={() => setEscolha(o)} icone={o === 'tarefa' ? <ListTodo className="size-4" aria-hidden /> : <MessageSquarePlus className="size-4" aria-hidden />}>
                  {o === 'tarefa' ? t('atalho.novaTarefa') : t('atalho.mensagemEquipe')}
                </Botao>
              ))}
            </div>
          )}
          {atual === 'tarefa' ? (
            <NovaTarefa
              aoSalvo={async (r, quadroId) => {
                fechar();
                toast.sucesso(t('atalho.tarefaCriada'), r.id ? { rotulo: t('atalho.verTarefa'), aoAcionar: () => nav(`/operacao/tarefas/kanban?quadro=${quadroId}&card=${r.id}`) } : undefined);
                await qc.invalidateQueries({ queryKey: CHAVE });
              }}
            />
          ) : (
            <NovaMensagem
              aoEnviada={(canalId) => {
                fechar();
                toast.sucesso(t('atalho.mensagemEnviada'), { rotulo: t('atalho.abrirConversa'), aoAcionar: () => nav(`/comunicacao/equipe/chat?canal=${canalId}`) });
                void qc.invalidateQueries({ queryKey: ['equipe'] });
              }}
            />
          )}
        </div>
      </Drawer>
    </>
  );
}

/**
 * Ajustes do CRM 27/10 (K12): o "Registro rápido" dentro do painel "Malgo IA" da casca nova (sem Drawer próprio): a escolha
 * entre tarefa e mensagem, o formulário e o botão de salvar ou enviar. `aoConcluir` fecha o painel depois do aviso.
 */
export function RegistroRapido({ aoConcluir }: { aoConcluir: () => void }) {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const nav = useNavigate();
  const opcoes = opcoesDoAtalho(s);
  const [escolha, setEscolha] = useState<OpcaoAtalho | null>(null);
  if (!opcoes.length) return null;
  const atual = escolha && opcoes.includes(escolha) ? escolha : opcoes[0]!;
  return (
    <div className="flex flex-col gap-4" data-registro-rapido="">
      {opcoes.length > 1 && (
        <div role="group" aria-label={t('atalho.oQue')} className="flex flex-wrap gap-2">
          {opcoes.map((o) => (
            <Botao key={o} variante="secundario" data-atalho-opcao={o} aria-pressed={atual === o} className={atual === o ? 'border-acento-forte' : ''} onClick={() => setEscolha(o)} icone={o === 'tarefa' ? <ListTodo className="size-4" aria-hidden /> : <MessageSquarePlus className="size-4" aria-hidden />}>
              {o === 'tarefa' ? t('atalho.novaTarefa') : t('atalho.mensagemEquipe')}
            </Botao>
          ))}
        </div>
      )}
      {atual === 'tarefa' ? (
        <NovaTarefa
          aoSalvo={async (r, quadroId) => {
            aoConcluir();
            toast.sucesso(t('atalho.tarefaCriada'), r.id ? { rotulo: t('atalho.verTarefa'), aoAcionar: () => nav(`/operacao/tarefas/kanban?quadro=${quadroId}&card=${r.id}`) } : undefined);
            await qc.invalidateQueries({ queryKey: CHAVE });
          }}
        />
      ) : (
        <NovaMensagem
          aoEnviada={(canalId) => {
            aoConcluir();
            toast.sucesso(t('atalho.mensagemEnviada'), { rotulo: t('atalho.abrirConversa'), aoAcionar: () => nav(`/comunicacao/equipe/chat?canal=${canalId}`) });
            void qc.invalidateQueries({ queryKey: ['equipe'] });
          }}
        />
      )}
      <div className="flex justify-end">
        <Botao variante="primario" type="submit" form={atual === 'tarefa' ? 'form-card' : 'form-atalho-mensagem'}>
          {atual === 'tarefa' ? t('atalho.salvarTarefa') : t('atalho.enviarMensagem')}
        </Botao>
      </div>
    </div>
  );
}
